/**
 * Settings API
 * Infrastructure Layer - External API communication
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';
import {
  SettingsDTO,
  UpdateSettingsDTO,
  ISaveFilterApiDto,
  IFilterSettingsApiDto,
  IContextSettingsApiDto,
  IUpsertContextSettingsDto,
} from '../../application/dto/Settings.dto';

/**
 * Settings API (RTK Query)
 * Infrastructure layer - handles HTTP communication
 */
export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Settings', 'FilterSettings', 'ContextSettings'],
  endpoints: build => ({
    getSettings: build.query<SettingsDTO, string>({
      query: userId => ({
        url: `/settings/${userId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: SettingsDTO }) => response.data,
      providesTags: ['Settings'],
    }),

    updateSettings: build.mutation<
      SettingsDTO,
      { userId: string; settings: UpdateSettingsDTO }
    >({
      query: ({ userId, settings }) => ({
        url: `/settings/${userId}`,
        method: 'PUT',
        body: settings,
      }),
      transformResponse: (response: { data: SettingsDTO }) => response.data,
      invalidatesTags: ['Settings'],
    }),

    // Filter endpoints
    getFilter: build.query<IFilterSettingsApiDto | null, string>({
      query: filterId => ({
        url: `settings/filter/${filterId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IFilterSettingsApiDto | null }) =>
        response.data,
      providesTags: (result, error, filterId) => [
        { type: 'FilterSettings', id: filterId },
      ],
    }),

    saveFilter: build.mutation<IFilterSettingsApiDto, ISaveFilterApiDto>({
      query: dto => ({
        url: 'settings/filter/save',
        method: 'POST',
        body: dto,
      }),
      transformResponse: (response: { data: IFilterSettingsApiDto }) =>
        response.data,
      invalidatesTags: (result, error, dto) => [
        { type: 'FilterSettings', id: dto.filterId },
      ],
    }),

    deleteFilter: build.mutation<boolean, string>({
      query: filterId => ({
        url: `settings/filter/${filterId}/reset`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, filterId) => [
        { type: 'FilterSettings', id: filterId },
      ],
    }),

    getContextSettings: build.query<IContextSettingsApiDto | null, string>({
      query: key => ({
        url: `/system/settings/${key}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IContextSettingsApiDto | null }) =>
        response.data,
      providesTags: (_result, _error, key) => [
        { type: 'ContextSettings', id: key },
      ],
    }),

    upsertContextSettings: build.mutation<
      IContextSettingsApiDto,
      IUpsertContextSettingsDto
    >({
      query: dto => ({
        url: `/system/settings/${dto.key}`,
        method: 'PATCH',
        body: dto,
      }),
      transformResponse: (response: { data: IContextSettingsApiDto }) =>
        response.data,
      async onQueryStarted(dto, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            settingsApi.util.updateQueryData(
              'getContextSettings',
              dto.key,
              () => data,
            ),
          );
        } catch {
          // Keep cached GET result if PATCH fails
        }
      },
    }),
  }),
});

export const {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useGetFilterQuery,
  useLazyGetFilterQuery,
  useSaveFilterMutation,
  useDeleteFilterMutation,
  useGetContextSettingsQuery,
  useUpsertContextSettingsMutation,
} = settingsApi;
